import { z } from "zod/v4";
import {
  CameraSubject,
  ClientApiFeaturePathParams,
  defineEndpoint,
  defineResource,
  SingleResponse,
} from "@visioni/a3s-client-api";
import { isoDatetimeToDateString } from "@visioni/a3s-codecs";
import { FEATURE_NAME } from "../constants";

/**
 * Page size for timelapse frame listing.
 *
 * Snapshots are expected hourly, so a single camera-local day fits in 24
 * frames. The viewer pages by day to keep memory and request size bounded.
 */
export const TIMELAPSE_FRAMES_PER_PAGE = 24;

/**
 * Maximum span allowed between `startTime` and `endTime` for any timelapse
 * request (frames or video). Hard cap protects the export pipeline and viewer
 * from unbounded ranges; the default UX still uses 7 days.
 */
export const TIMELAPSE_MAX_RANGE_DAYS = 14;

/**
 * Maximum range expressed in milliseconds. Exported so wire-schema validation
 * (this file) and service-level validation (`media-service.ts`) reference a
 * single source of truth.
 */
export const TIMELAPSE_MAX_RANGE_MS = TIMELAPSE_MAX_RANGE_DAYS * 24 * 60 * 60 * 1000;

const TIMELAPSE_MAX_RANGE_MESSAGE = `endTime - startTime must be <= ${String(TIMELAPSE_MAX_RANGE_DAYS)} days`;

/**
 * Camera timezone is the source of truth for day boundaries and "latest
 * available image" semantics. The viewer timezone is informational only —
 * it never changes which frames are returned.
 *
 * IANA timezone identifiers (e.g. `Australia/Sydney`); validation is
 * performed in handlers via the standard `Intl.DateTimeFormat` constructor
 * to avoid duplicating tz-database lists in the wire schema.
 */
const TimezoneId = z.string().min(1);

// ==================== Path Params ====================

export type ListTimelapseFramesPathParams = ClientApiFeaturePathParams;
export type RequestTimelapseVideoPathParams = ClientApiFeaturePathParams;
export type GetTimelapseVideoPathParams = ClientApiFeaturePathParams;

// ==================== Frame Listing ====================

/**
 * Query params for listing timelapse frames.
 *
 * `startTime`/`endTime` are mandatory so future UX (alternate week pickers,
 * date jumps) can switch buckets without API changes; the app currently
 * defaults them to the last seven camera-local days.
 *
 * `cursor` is an opaque day-cursor returned by the previous page; clients
 * call `loadMore` to append the next 24-hour slice.
 */
export const ListTimelapseFramesQueryParams = z
  .object({
    startTime: isoDatetimeToDateString,
    endTime: isoDatetimeToDateString,
    cameraTimezone: TimezoneId,
    viewerTimezone: TimezoneId.optional(),
    cursor: z.string().optional(),
  })
  .refine((q) => new Date(q.startTime).getTime() <= new Date(q.endTime).getTime(), {
    message: "startTime must be <= endTime",
    path: ["endTime"],
  })
  .refine((q) => new Date(q.endTime).getTime() - new Date(q.startTime).getTime() <= TIMELAPSE_MAX_RANGE_MS, {
    message: TIMELAPSE_MAX_RANGE_MESSAGE,
    path: ["endTime"],
  });

export type ListTimelapseFramesQueryParams = z.output<typeof ListTimelapseFramesQueryParams>;

/**
 * A signed URL to a single snapshot frame, ready for `<img>` rendering with
 * the CDN signature headers attached.
 */
export const TimelapseSignedFrame = z.object({
  url: z.string(),
  headers: z.object({
    "X-Media-Signature": z.string(),
    "X-Media-Expires": z.string(),
  }),
});

export type TimelapseSignedFrame = z.output<typeof TimelapseSignedFrame>;

export const TimelapseFrameItem = z.object({
  subject: CameraSubject,
  capturedAt: z.string(),
  frame: TimelapseSignedFrame,
});

export type TimelapseFrameItem = z.output<typeof TimelapseFrameItem>;

/**
 * Frame listing response.
 *
 * `cursor` is opaque (day-pagination key consumed by subsequent requests).
 * `hasMore` is provided as an explicit flag because the viewer drives a
 * "Load more" affordance and shouldn't have to differentiate between a
 * trailing empty page and a finished range.
 */
export const ListTimelapseFramesResponse = z.object({
  data: z.array(TimelapseFrameItem),
  cursor: z.string().optional(),
  hasMore: z.boolean(),
});

export type ListTimelapseFramesResponse = z.output<typeof ListTimelapseFramesResponse>;

// ==================== Video Export ====================

/**
 * Status states for the asynchronous timelapse export job.
 *
 * - `queued`: job accepted, waiting for the renderer worker
 * - `processing`: renderer is downloading frames / running ffmpeg
 * - `ready`: MP4 is uploaded; status response includes a signed URL
 * - `failed`: rendering failed; status response includes a failure reason
 */
export const TimelapseVideoStatus = z.enum(["queued", "processing", "ready", "failed"]);

export type TimelapseVideoStatus = z.output<typeof TimelapseVideoStatus>;

/**
 * Body for `POST /timelapse/video`. Mirrors `ListTimelapseFramesQueryParams`
 * for the windowing fields so the export uses the same camera-time-derived
 * UTC range as the viewer.
 */
export const RequestTimelapseVideoBody = z
  .object({
    startTime: isoDatetimeToDateString,
    endTime: isoDatetimeToDateString,
    cameraTimezone: TimezoneId,
  })
  .refine((b) => new Date(b.startTime).getTime() <= new Date(b.endTime).getTime(), {
    message: "startTime must be <= endTime",
    path: ["endTime"],
  })
  .refine((b) => new Date(b.endTime).getTime() - new Date(b.startTime).getTime() <= TIMELAPSE_MAX_RANGE_MS, {
    message: TIMELAPSE_MAX_RANGE_MESSAGE,
    path: ["endTime"],
  });

export type RequestTimelapseVideoBody = z.output<typeof RequestTimelapseVideoBody>;

export const TimelapseVideoSignedUrl = z.object({
  url: z.string(),
  expiresAt: z.string(),
});

export type TimelapseVideoSignedUrl = z.output<typeof TimelapseVideoSignedUrl>;

export const TimelapseVideoItem = z.object({
  subject: CameraSubject,
  status: TimelapseVideoStatus,
  startTime: z.string(),
  endTime: z.string(),
  cameraTimezone: TimezoneId,
  /** Signed download URL — only set when `status === "ready"`. */
  video: TimelapseVideoSignedUrl.optional(),
  /** Human-readable failure reason — only set when `status === "failed"`. */
  failureReason: z.string().optional(),
  requestedAt: z.string(),
  updatedAt: z.string(),
});

export type TimelapseVideoItem = z.output<typeof TimelapseVideoItem>;

export const RequestTimelapseVideoResponse = SingleResponse(TimelapseVideoItem);
export type RequestTimelapseVideoResponse = z.output<typeof RequestTimelapseVideoResponse>;

/**
 * Query params for `GET /timelapse/video`. The same window identifies the
 * job, so the GET request must echo the original `startTime`/`endTime`/
 * `cameraTimezone` triple used at request time.
 */
export const GetTimelapseVideoQueryParams = z
  .object({
    startTime: isoDatetimeToDateString,
    endTime: isoDatetimeToDateString,
    cameraTimezone: TimezoneId,
  })
  .refine((q) => new Date(q.startTime).getTime() <= new Date(q.endTime).getTime(), {
    message: "startTime must be <= endTime",
    path: ["endTime"],
  })
  .refine((q) => new Date(q.endTime).getTime() - new Date(q.startTime).getTime() <= TIMELAPSE_MAX_RANGE_MS, {
    message: TIMELAPSE_MAX_RANGE_MESSAGE,
    path: ["endTime"],
  });

export type GetTimelapseVideoQueryParams = z.output<typeof GetTimelapseVideoQueryParams>;

export const GetTimelapseVideoResponse = SingleResponse(TimelapseVideoItem);
export type GetTimelapseVideoResponse = z.output<typeof GetTimelapseVideoResponse>;

// ==================== Resources & Endpoints ====================

export const timelapseFramesResource = defineResource({
  feature: FEATURE_NAME,
  resource: "timelapse/frames",
});

export const listTimelapseFramesEndpoint = defineEndpoint(timelapseFramesResource, {
  responseSchema: ListTimelapseFramesResponse,
  querySchema: ListTimelapseFramesQueryParams,
});

export const timelapseVideoResource = defineResource({
  feature: FEATURE_NAME,
  resource: "timelapse/video",
});

export const requestTimelapseVideoEndpoint = defineEndpoint(timelapseVideoResource, {
  method: "POST",
  bodySchema: RequestTimelapseVideoBody,
  responseSchema: RequestTimelapseVideoResponse,
});

export const getTimelapseVideoEndpoint = defineEndpoint(timelapseVideoResource, {
  responseSchema: GetTimelapseVideoResponse,
  querySchema: GetTimelapseVideoQueryParams,
});
